/**
 * The macro engine — definitions and expansion, driven by data.
 *
 * A macro is NOT a runtime op. Authoring one emits plain standard nodes
 * (`flow/sequence`, `flow/setDelay`, …) that any conformant KHR_interactivity
 * viewer executes without Trace extensions — composition over new ops
 * (DESIGN_PRINCIPLES §6). What makes the group collapsible back into a single
 * logical block is author metadata: every node of an expansion carries
 * `{ traceMacro, macroId, macroRole }` tags in its `metadata`, which the
 * runtime ignores, `.trace` round-trips natively, and the KHR converters carry
 * through glTF `extras` (the designated user-data channel).
 *
 * Two representations, one definition:
 *
 *   - RAISED — one `macro/<name>` pseudo-node per instance, carrying the
 *     block's params, flow inputs/outputs and `macroId`. What the editor
 *     shows and what the AI assistant reads and writes. Never stored.
 *   - LOWERED — the tagged canonical expansion of standard ops. What is stored,
 *     executed and exported.
 *
 * `lowerMacros` (lower.ts) goes raised → lowered; `raiseMacros` (recognize.ts)
 * goes lowered → raised. Recognition is by tags, and a tagged group is raised
 * only when re-expanding its raised form reproduces it exactly — one generic
 * check instead of per-feature coherence rules, so a group another tool edited
 * degrades to plain nodes rather than being silently "repaired" by a lossy
 * raise. BehaviorGraph lowers at its data-ingestion choke points (constructor +
 * commit validate), so pseudo-ops never reach stored GraphData.
 *
 * ── Adding a macro ──
 *
 * A macro is a `MacroDefinition` — pure data, no code. Declare the nodes of
 * its canonical expansion, the wires between them, which internal sockets the
 * logical block's inputs/outputs map to, and which params land on which
 * internal value sockets. Everything else (expand, recognize, raise, lower,
 * disarm, legacy upgrade) is generic over that description. Register it and
 * add the matching catalog entry; see `interval.ts` for the reference
 * instance. When a canonical expansion changes, move the old one into
 * `legacyShapes` — stored data in that shape is then raised with it and
 * lowered with the current one at ingestion, with no bespoke migration.
 */

import { Logger } from '../../../utils/Logger';
import type { FlowTarget, TypeSignature, ValueInput } from '../../../scenedata/behaviors/behaviorgraphdata';

// ============================================================================
// Metadata tags
// ============================================================================

/** Metadata key marking a node as part of a macro expansion. */
export const TRACE_MACRO_KEY = 'traceMacro';
/** Metadata key carrying the shared instance id of one expansion. */
export const MACRO_ID_KEY = 'macroId';
/** Metadata key carrying the node's role within the expansion. */
export const MACRO_ROLE_KEY = 'macroRole';

/**
 * Prefix of catalog pseudo-ops (`macro/…`) — names for logical blocks in the
 * palette and in high-level authoring. Never valid in stored GraphData.
 */
export const MACRO_OP_PREFIX = 'macro/';

/** Configuration key of `event/send` / `event/receive` naming the event index. */
export const MACRO_EVENT_CONFIG = 'event';
/** Configuration key of `variable/get` / `variable/set` naming the variable index. */
export const MACRO_VARIABLE_CONFIG = 'variable';

// ============================================================================
// Definition
// ============================================================================

/**
 * Minimal graph-writer surface an expansion needs. `GraphBuilder` satisfies
 * it structurally (via its `@internal` helpers) — a structural interface here
 * avoids a module cycle between GraphBuilder and this file.
 */
export interface MacroGraphWriter {
    _addNode(op: string, config: Record<string, unknown[]>, metadata?: Record<string, unknown>): number;
    _setFlow(fromNode: number, fromSocket: string, toNode: number, toSocket?: string): void;
    _setValue(nodeIndex: number, socket: string, value: unknown): void;
    _setValueRef(nodeIndex: number, socket: string, sourceNode: number, sourceSocket?: string): void;
    /** Append a value-less INTERNAL custom event (no id) to the graph; returns its index. */
    _addEvent(): number;
    /** Append a graph variable; returns its index. */
    _addVariable(name: string, signature: TypeSignature, value: unknown): number;
}

/** One node of a canonical expansion. */
export interface MacroNodeDef {
    /** Role name — the `macroRole` tag, unique within the expansion. */
    role: string;
    /** Standard op this role's node carries. */
    op: string;
    /**
     * Constant inline literals on this node's value inputs (e.g. the `true`
     * a flag's `variable/set` writes). Part of the composition, not a param
     * of the collapsed block.
     */
    constants?: Readonly<Record<string, boolean | number | string>>;
}

/** A flow wire between two roles of an expansion. */
export interface MacroWireDef {
    from: string;
    socket: string;
    to: string;
    toSocket: string;
}

/** A data connection between two roles of an expansion. */
export interface MacroValueWireDef {
    /** Role producing the value. */
    from: string;
    /** Its output socket. */
    socket: string;
    /** Role consuming it. */
    to: string;
    /** Its input socket. */
    toSocket: string;
}

/**
 * A custom event private to ONE expansion instance. Expanding appends a fresh
 * internal, value-less event to `GraphData.events` and points the listed
 * roles' `event` configuration at it, so two instances never share an event
 * (no cross-talk). This is how a macro repeats without a flow cycle: an
 * `event/send` re-enters the graph at an `event/receive` entry node.
 */
export interface MacroEventDef {
    /** `event/send` / `event/receive` roles bound to this instance's event. */
    roles: readonly string[];
}

/**
 * A graph variable private to ONE expansion instance — state the composition
 * keeps between events. Expanding appends a fresh variable and points the
 * listed roles' `variable` configuration at it. The native runtime addresses
 * variables by NAME, so the name is made unique per instance:
 * `<macro>:<macroId>:<name>`.
 */
export interface MacroVariableDef {
    name: string;
    type: TypeSignature;
    initial: boolean | number | string;
    /** `variable/get` / `variable/set` roles bound to this instance's variable. */
    roles: readonly string[];
}

/** A socket inside the expansion, addressed by role. */
export interface MacroSocketRef {
    role: string;
    socket: string;
}

/** A value input of the collapsed block. */
export interface MacroParamDef {
    /** Sockets inside the expansion this parameter's value is written to. */
    targets: readonly MacroSocketRef[];
    /**
     * Where the value lands. `'value'` (the default) writes a data input that
     * can also be driven by a wire; `'config'` writes node configuration —
     * identity/addressing params like a `target` path, which are static by
     * nature and cannot be wired.
     */
    kind?: 'value' | 'config';
    /**
     * Reject an inline literal, returning the reason. A rejected param
     * disarms the expansion (`disarm`) rather than emitting a live but broken
     * composition. Wired (node-ref) params cannot be checked here.
     */
    validateLiteral?(value: unknown): string | null;
}

/**
 * The structure of one canonical expansion: its nodes, internal wiring,
 * private state, and how the collapsed block's sockets and params map onto it.
 * Recognition compares a stored group against a re-expansion of this shape, so
 * everything declared here is checked — nothing needs a rule of its own.
 */
export interface MacroShape {
    /** Nodes of the canonical expansion, each role present exactly once. */
    nodes: readonly MacroNodeDef[];
    /** Flow wires of the canonical expansion. */
    wires: readonly MacroWireDef[];
    /** Data connections of the canonical expansion. */
    valueWires?: readonly MacroValueWireDef[];
    /** Private custom events of the expansion (see `MacroEventDef`). */
    events?: readonly MacroEventDef[];
    /** Private variables of the expansion (see `MacroVariableDef`). */
    variables?: readonly MacroVariableDef[];
    /** Role standing in for the collapsed block (carries its author metadata). */
    entryRole: string;
    /** Flow inputs of the collapsed block → where flow enters the expansion. */
    inputs: Readonly<Record<string, MacroSocketRef>>;
    /** Input an inbound flow naming an unknown socket falls back to. */
    defaultInput: string;
    /** Flow outputs of the collapsed block → where downstream chains attach. */
    outputs: Readonly<Record<string, MacroSocketRef>>;
    /** Value inputs of the collapsed block. */
    params: Readonly<Record<string, MacroParamDef>>;
    /**
     * Wires to drop when ANY param fails validation, leaving the expansion
     * inert. Stored data stays standard-ops-only; the composition simply
     * never runs.
     *
     * Deliberately macro-level rather than per-param: a composition with a
     * bad input should not run at all, so there is one inert state to reason
     * about rather than a matrix of partially-live ones.
     */
    disarm: readonly { from: string; socket: string }[];
}

/**
 * A canned composition, described declaratively. The engine derives expansion,
 * recognition, raising, lowering, and disarming from this description — a
 * macro contributes data, never behavior.
 */
export interface MacroDefinition extends MacroShape {
    /** `traceMacro` tag value, and the suffix of the catalog pseudo-op. */
    name: string;
    /** Catalog pseudo-op (`macro/<name>`). */
    op: string;
    /**
     * Superseded canonical expansions, newest first. A stored group matching
     * one is still recognized (and raised) as this macro, and ingestion
     * re-lowers it with the current shape — the migration is data, not code.
     * Each must keep the block's params, inputs and outputs by name.
     */
    legacyShapes?: readonly MacroShape[];
}

/** Node indices of one expansion, keyed by role. */
export type MacroRoleNodes = Readonly<Record<string, number>>;

/** One emitted expansion: its instance id and the nodes it created. */
export interface MacroExpansion {
    macroId: string;
    nodes: MacroRoleNodes;
    /** `GraphData.events` indices of the private events, in `def.events` order. */
    events: number[];
    /** `GraphData.variables` indices of the private variables, in `def.variables` order. */
    variables: number[];
}

/** A value input wired from another node. */
export type MacroWiredValue = Extract<ValueInput, { node: number }>;

/** A recognized expansion within a GraphData. */
export interface MacroInstance {
    /** Macro name (`MacroDefinition.name`). */
    macro: string;
    macroId: string;
    /** Node indices by role. */
    nodes: MacroRoleNodes;
    /** Inline literal (and config) params by name; a wired param is in `wired` instead. */
    params: Readonly<Record<string, unknown>>;
    /** Params wired from another node, by name. */
    wired: Readonly<Record<string, MacroWiredValue>>;
    /** Outbound flow targets by logical output socket, where wired. */
    outputs: Readonly<Record<string, FlowTarget>>;
    /** `GraphData.events` indices of the private events, in definition order. */
    events: readonly number[];
    /** `GraphData.variables` indices of the private variables, in definition order. */
    variables: readonly number[];
    /** True when the group is in a superseded shape (`legacyShapes`); ingestion re-lowers it. */
    legacy: boolean;
}

// ============================================================================
// Registry
// ============================================================================

const REGISTRY = new Map<string, MacroDefinition>();

/**
 * Marker for a parameter supplied by a wire rather than an inline literal.
 *
 * A wired value cannot be validated at authoring time — its value is not known
 * until the graph runs — so a marked param is neither validated nor written;
 * the caller attaches the node-ref itself. This is distinct from a param that
 * is simply ABSENT, which fails validation like any other bad value.
 */
export const MACRO_PARAM_WIRED = Symbol('macro-param-wired');

/** Register a macro definition. Registration is by name; ops must be unique too. */
export function registerMacro(def: MacroDefinition): void {
    const existing = REGISTRY.get(def.name);
    if (existing && existing !== def) {
        Logger.warn(`[macros] macro "${def.name}" re-registered — the previous definition is replaced`);
    }
    REGISTRY.set(def.name, def);
}

/** The macro definition of the given name, or undefined. */
export function getMacro(name: string): MacroDefinition | undefined {
    return REGISTRY.get(name);
}

/** The macro definition owning the given catalog pseudo-op, or undefined. */
export function getMacroByOp(op: string): MacroDefinition | undefined {
    for (const def of REGISTRY.values()) {
        if (def.op === op) return def;
    }
    return undefined;
}

/** All registered macro definitions. */
export function allMacros(): MacroDefinition[] {
    return [...REGISTRY.values()];
}

// ============================================================================
// Expansion
// ============================================================================

function macroMeta(name: string, macroId: string, role: string): Record<string, unknown> {
    return { [TRACE_MACRO_KEY]: name, [MACRO_ID_KEY]: macroId, [MACRO_ROLE_KEY]: role };
}

/** Options for {@link expandMacro}. */
export interface ExpandMacroOptions {
    /**
     * Expand a superseded shape of `def` instead of its current one
     * (recognition re-expands legacy groups to compare them).
     */
    shape?: MacroShape;
    /** Do not log rejected params (recognition re-expands stored, already-reported data). */
    silent?: boolean;
}

/**
 * Emit a macro's canonical expansion into a graph writer.
 *
 * @param writer Graph writer (typically a `GraphBuilder`).
 * @param def The macro to expand.
 * @param params Inline literal values for the macro's value inputs, keyed by
 *   param name. A param failing `validateLiteral` disarms the expansion; pass
 *   `MACRO_PARAM_WIRED` for one the caller will wire from another node.
 * @param macroId Shared instance id; generated when omitted.
 */
export function expandMacro(
    writer: MacroGraphWriter,
    def: MacroDefinition,
    params: Readonly<Record<string, unknown>> = {},
    macroId?: string,
    options: ExpandMacroOptions = {},
): MacroExpansion {
    const id = macroId ?? crypto.randomUUID();
    const shape = options.shape ?? def;

    // Config-kind params are addressing/identity fields that belong on the
    // node at creation, so gather them per role before emitting.
    const configByRole: Record<string, Record<string, unknown[]>> = {};
    for (const [name, param] of Object.entries(shape.params)) {
        if (param.kind !== 'config') continue;
        const value = params[name];
        if (value === undefined || value === MACRO_PARAM_WIRED) continue;
        for (const target of param.targets) {
            (configByRole[target.role] ??= {})[target.socket] = [value];
        }
    }

    // Each private event is a fresh graph event; its roles address it by index.
    const events: number[] = [];
    for (const event of shape.events ?? []) {
        const index = writer._addEvent();
        events.push(index);
        for (const role of event.roles) (configByRole[role] ??= {})[MACRO_EVENT_CONFIG] = [index];
    }

    const variables: number[] = [];
    for (const variable of shape.variables ?? []) {
        const index = writer._addVariable(macroVariableName(def, id, variable), variable.type, variable.initial);
        variables.push(index);
        for (const role of variable.roles) (configByRole[role] ??= {})[MACRO_VARIABLE_CONFIG] = [index];
    }

    const nodes: Record<string, number> = {};
    for (const node of shape.nodes) {
        nodes[node.role] = writer._addNode(node.op, configByRole[node.role] ?? {}, macroMeta(def.name, id, node.role));
        for (const [socket, value] of Object.entries(node.constants ?? {})) writer._setValue(nodes[node.role], socket, value);
    }

    // Iterate the DECLARED params, not the supplied ones: a param the caller
    // omitted entirely is exactly as broken as one with a bad value, and
    // skipping it would emit a live expansion with an unset socket.
    let disarmed = false;
    for (const [name, param] of Object.entries(shape.params)) {
        const value = params[name];
        if (value === MACRO_PARAM_WIRED) continue; // unknowable until it runs
        const reason = param.validateLiteral?.(value) ?? null;
        if (reason !== null) {
            // ERROR, not WARN: Logger.level defaults to ERROR, and a rejected
            // param means the emitted composition will not work. An authoring
            // defect that produces dead output must not be invisible.
            if (!options.silent) Logger.error(`[macros] ${def.op}: ${name} ${reason} — expansion not armed`);
            disarmed = true;
        }
        if (value === undefined || param.kind === 'config') continue; // config already applied at creation
        for (const target of param.targets) writer._setValue(nodes[target.role], target.socket, value);
    }

    for (const wire of shape.valueWires ?? []) {
        writer._setValueRef(nodes[wire.to], wire.toSocket, nodes[wire.from], wire.socket);
    }

    const dropped = disarmed ? shape.disarm : [];
    for (const wire of shape.wires) {
        if (dropped.some(d => d.from === wire.from && d.socket === wire.socket)) continue;
        writer._setFlow(nodes[wire.from], wire.socket, nodes[wire.to], wire.toSocket);
    }

    return { macroId: id, nodes, events, variables };
}

/** Name of an instance's private variable — unique, since the runtime addresses variables by name. */
export function macroVariableName(def: MacroDefinition, macroId: string, variable: MacroVariableDef): string {
    return `${def.name}:${macroId}:${variable.name}`;
}
